import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { buildBackendLoginUrl, currentReturnPath, fetchAuthSession, logoutAuthSession, preferredUserLabel } from '@aide/auth-client'
import type { AuthSessionData } from '@aide/platform-types'
import api from '../services/api'
import { bindChatStoreIdentity } from '../store/chatStore'
import { useArtifactStore } from '../store/artifactStore'
import { useDatasourceStore } from '../store/datasourceStore'

interface AuthContextType {
  authenticated: boolean
  user: string | null
  claims: Record<string, unknown> | null
  profile: AuthSessionData | null
  loading: boolean
  login: (nextPath?: string) => Promise<void>
  logout: () => Promise<void>
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<AuthSessionData | null>(null)
  const [claims, setClaims] = useState<Record<string, unknown> | null>(null)
  const [loading, setLoading] = useState(true)
  const authRequest = useRef(0)
  const transportIdentity = useRef<string | null>(null)

  const clearAuth = useCallback(() => {
    transportIdentity.current = null
    useArtifactStore.getState().closeAllTabs()
    useDatasourceStore.getState().clear()
    bindChatStoreIdentity(null, null)
    setProfile(null)
    setClaims(null)
    api.setToken(null)
  }, [])

  const refresh = useCallback(async () => {
    const request = ++authRequest.current
    setLoading(true)
    try {
      const session = await fetchAuthSession()
      if (request !== authRequest.current) return
      if (!session?.authenticated || !session.tenant_id?.trim() || !session.id?.trim()) {
        clearAuth()
        return
      }
      const identity = JSON.stringify([session.tenant_id, session.id])
      if (identity !== transportIdentity.current) {
        api.setToken(null)
        useArtifactStore.getState().closeAllTabs()
        useDatasourceStore.getState().clear()
      }
      transportIdentity.current = identity
      bindChatStoreIdentity(session.tenant_id, session.id)
      setProfile(session)
      setClaims(session.claims || null)
    } catch {
      if (request === authRequest.current) clearAuth()
    } finally {
      if (request === authRequest.current) setLoading(false)
    }
  }, [clearAuth])

  useEffect(() => {
    void refresh()
    return () => { authRequest.current += 1 }
  }, [refresh])

  const login = useCallback(async (nextPath?: string) => {
    authRequest.current += 1
    clearAuth()
    window.location.assign(buildBackendLoginUrl('frontend', nextPath || currentReturnPath()))
  }, [clearAuth])

  const logout = useCallback(async () => {
    const request = ++authRequest.current
    clearAuth()
    setLoading(false)
    try {
      await logoutAuthSession()
    } finally {
      if (request === authRequest.current) clearAuth()
    }
  }, [clearAuth])

  return (
    <AuthContext.Provider
      value={{
        authenticated: Boolean(profile?.authenticated),
        user: preferredUserLabel(profile),
        claims,
        profile,
        loading,
        login,
        logout,
        refresh,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
